import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Users, BookOpen, Video, FolderTree } from "lucide-react"
import { userService } from "@/services/user.service"
import { courseService } from "@/services/course.service"
import { videoService } from "@/services/video.service"

export default function DashboardPage() {
  const [stats, setStats] = useState({
    users: 0,
    courses: 0,
    videos: 0,
    categories: 0,
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [usersData, coursesData, videosData, categoriesData] =
          await Promise.all([
            userService.getUsers({ limit: 1 }),
            courseService.getCourses({ limit: 1 }),
            videoService.getVideos({ limit: 1 }),
            courseService.getCategories({ limit: 1 }),
          ])

        setStats({
          users: usersData.count,
          courses: coursesData.count,
          videos: videosData.count,
          categories: categoriesData.count,
        })
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [])

  const statCards = [
    {
      title: "Всего пользователей",
      value: stats.users,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      title: "Всего курсов",
      value: stats.courses,
      icon: BookOpen,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      title: "Всего видео",
      value: stats.videos,
      icon: Video,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      title: "Всего категорий",
      value: stats.categories,
      icon: FolderTree,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Панель управления</h1>
        <p className="text-gray-600 mt-1">Добро пожаловать в панель управления Skylls</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {isLoading ? (
                  <div className="h-8 w-16 bg-gray-200 animate-pulse rounded" />
                ) : (
                  stat.value.toLocaleString()
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Быстрые действия</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Используйте боковую панель для навигации по различным разделам панели управления.
            </p>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>Управление пользователями и их правами</li>
              <li>Создание и редактирование курсов</li>
              <li>Загрузка и управление видео</li>
              <li>Организация контента с помощью категорий и тем</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Информация о системе</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Статус API</dt>
                <dd className="font-medium text-green-600">Подключено</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Бэкенд</dt>
                <dd className="font-medium">Django REST Framework</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Видеоплатформа</dt>
                <dd className="font-medium">VdoCipher</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
